import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const RecentActivityFeed = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecentActivity();
  }, []);

  const fetchRecentActivity = async () => {
    try {
      setLoading(true);
      const activityList = [];

      // Fetch recent inventory items (last 10)
      const { data: inventoryData, error: inventoryError } = await supabase
        ?.from('lasea_inventory')
        ?.select(`
          id,
          item_name,
          created_at,
          updated_at,
          client:lasea_users!client_id (
            company_name,
            full_name
          )
        `)
        ?.order('created_at', { ascending: false })
        ?.limit(5);

      if (inventoryError) throw inventoryError;

      // Add inventory items to activity list
      inventoryData?.forEach(item => {
        activityList?.push({
          id: `inv-${item?.id}`,
          type: 'add',
          user: 'Admin',
          action: 'added inventory item',
          itemName: item?.item_name,
          client: item?.client?.company_name || item?.client?.full_name || 'Unknown Client',
          timestamp: formatTimestamp(item?.created_at),
          icon: 'Package',
          color: 'primary'
        });
      });

      // Fetch recent photos (last 10)
      const { data: photoData, error: photoError } = await supabase
        ?.from('lasea_inventory_photos')
        ?.select(`
          id,
          created_at,
          inventory:lasea_inventory!inventory_id (
            item_name,
            client:lasea_users!client_id (
              company_name,
              full_name
            )
          )
        `)
        ?.order('created_at', { ascending: false })
        ?.limit(5);

      if (photoError) throw photoError;

      // Add photo uploads to activity list
      photoData?.forEach(photo => {
        activityList?.push({
          id: `photo-${photo?.id}`,
          type: 'photo',
          user: 'Admin',
          action: 'uploaded photo for',
          itemName: photo?.inventory?.item_name,
          client: photo?.inventory?.client?.company_name || photo?.inventory?.client?.full_name || 'Unknown Client',
          timestamp: formatTimestamp(photo?.created_at),
          icon: 'Camera',
          color: 'success'
        });
      });

      // Sort all activities by timestamp (most recent first)
      activityList?.sort((a, b) => {
        const timeA = parseTimestamp(a?.timestamp);
        const timeB = parseTimestamp(b?.timestamp);
        return timeB - timeA;
      });

      // Take only the 5 most recent activities
      setActivities(activityList?.slice(0, 5));
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffMs = now - activityTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return activityTime?.toLocaleDateString();
  };

  const parseTimestamp = (formattedTime) => {
    // Helper to convert formatted timestamp back to Date for sorting
    if (formattedTime === 'Just now') return new Date();
    if (formattedTime?.includes('minute')) {
      const mins = parseInt(formattedTime);
      return new Date(Date.now() - mins * 60000);
    }
    if (formattedTime?.includes('hour')) {
      const hours = parseInt(formattedTime);
      return new Date(Date.now() - hours * 3600000);
    }
    if (formattedTime?.includes('day')) {
      const days = parseInt(formattedTime);
      return new Date(Date.now() - days * 86400000);
    }
    return new Date(formattedTime);
  };

  const getColorClasses = (color) => {
    const colorMap = {
      success: 'bg-success/10 text-success',
      accent: 'bg-accent/10 text-accent',
      primary: 'bg-primary/10 text-primary',
      warning: 'bg-warning/10 text-warning',
      error: 'bg-error/10 text-error'
    };
    return colorMap?.[color] || colorMap?.primary;
  };

  return (
    <div className="bg-card rounded-xl p-6 border border-border">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Icon name="Activity" size={24} color="var(--color-primary)" />
          <h2 className="text-xl font-semibold text-foreground">Recent Activity</h2>
        </div>
      </div>
      
      {loading ? (
        <div className="text-center py-12">
          <p className="text-sm text-muted-foreground">Loading activity...</p>
        </div>
      ) : activities?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="Inbox" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No recent activity</p>
        </div>
      ) : (
        <div className="space-y-4">
          {activities?.map((activity, index) => (
            <div
              key={activity?.id}
              className={`flex items-start gap-4 pb-4 ${
                index !== activities?.length - 1 ? 'border-b border-border' : ''
              }`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${getColorClasses(activity?.color)}`}>
                <Icon name={activity?.icon} size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground mb-1">
                  <span className="font-medium">{activity?.user}</span>
                  {' '}
                  <span className="text-muted-foreground">{activity?.action}</span>
                  {activity?.itemName && (
                    <>
                      {' '}
                      <span className="font-medium">"{activity?.itemName}"</span>
                    </>
                  )}
                  {' for '}
                  <span className="font-medium">{activity?.client}</span>
                </p>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Icon name="Clock" size={12} />
                  {activity?.timestamp}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RecentActivityFeed;